import { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, ArrowLeft, Target, UserPlus, Trash2, X, Users, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogSheetContent, DialogSheetHeader, DialogSheetBody, DialogDescription } from '../components/ui/dialog';
import EmptyState from '../components/EmptyState';
import { formatAmount, formatRelativeTime, getIcon, categoryColors, goalIcons, goalPeriods, catColor, catTint } from '../lib/constants';
import { getGoal, createGoal, updateGoal, deleteGoal, leaveGoal, contributeToGoal, deleteTransaction, nudgeGoalMember, isPaywallError } from '../lib/api';
import { useAmountsVisibility } from '../context/AmountsVisibilityContext';
import { usePaywall } from '../context/PaywallContext';
import { ProfileButton } from '../components/Sidebar';
import { friendlyError } from '../lib/errorReporting';

// Metas — personal or shared savings goals. Contributions ("aportes") lower
// your balance without counting as spending (see server.py GOALS section).
// Layout follows design_guidelines.json: list rows + spacing (no card per
// goal), a Balance-style hero on the detail view, and sub-page navigation
// with a back arrow like Categorías.

const periodPer = (p) => goalPeriods.find(x => x.value === p)?.per || 'mes';
// Local calendar date (not UTC — late evening would otherwise read as tomorrow).
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const formatTargetDate = (d) => d
  ? new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${d}T12:00:00`))
  : null;

function daysLeftLabel(goal) {
  if (goal.completed) return '¡Meta cumplida!';
  if (goal.days_left === null || goal.days_left === undefined) return null;
  if (goal.days_left < 0) return 'Fecha vencida';
  if (goal.days_left === 0) return 'Vence hoy';
  return `Faltan ${goal.days_left} ${goal.days_left === 1 ? 'día' : 'días'}`;
}

// Share an invite link: native share sheet (WhatsApp etc.) on phones,
// clipboard copy elsewhere.
async function shareInvite(goal) {
  const text = `Únete a mi meta "${goal.name}" en MiPisto y ahorremos juntos.`;
  if (navigator.share) {
    try {
      await navigator.share({ title: 'MiPisto', text, url: goal.invite_link });
      return;
    } catch (e) {
      if (e?.name === 'AbortError') return;
    }
  }
  try {
    await navigator.clipboard.writeText(`${text} ${goal.invite_link}`);
    toast.success('Enlace de invitación copiado');
  } catch {
    toast.message(goal.invite_link);
  }
}

function Avatar({ name, photo, size = 'w-9 h-9' }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [photo]);
  if (photo && !failed) return <img src={photo} alt="" onError={() => setFailed(true)} className={`${size} rounded-full object-cover shrink-0`} />;
  return (
    <div className={`${size} rounded-full shrink-0 flex items-center justify-center bg-violet-500/15 text-violet-300 text-sm font-semibold`}>
      {(name || '?').trim().charAt(0).toUpperCase()}
    </div>
  );
}

function ProgressTrack({ pct, color, height = 'h-1.5' }) {
  return (
    <div className={`relative ${height} w-full overflow-hidden rounded-full bg-white/[0.04] ring-1 ring-inset ring-white/10`}>
      <div
        className="h-full rounded-full budget-progress-bar"
        style={{ transform: `scaleX(${Math.min(pct, 100) / 100})`, backgroundColor: color }}
      />
    </div>
  );
}

export default function Goals({ goals, onRefresh, openGoalId, setOpenGoalId, refreshKey }) {
  const [showCreate, setShowCreate] = useState(false);

  if (openGoalId) {
    return <GoalDetail goalId={openGoalId} onBack={() => setOpenGoalId(null)} onRefresh={onRefresh} refreshKey={refreshKey} />;
  }

  return (
    <div className="space-y-8" data-testid="goals-page">
      {/* Same header shape as Movimientos / Análisis: title and actions in
          one row (aligned to the avatar), the subtitle on its own line. */}
      <div>
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white min-w-0 truncate">Metas</h1>
        <div className="flex items-center gap-2 shrink-0">
        {goals.length > 0 && (
          <Button
            data-testid="add-goal-btn"
            onClick={() => setShowCreate(true)}
            className="hit-44 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full h-10 px-5 shrink-0 active:scale-[0.97]"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Nueva
          </Button>
        )}
        <ProfileButton className="-mr-1.5" />
        </div>
      </div>
      <p className="text-slate-400 text-sm mt-1">Ahorra para lo que quieres, solo o con otras personas</p>
      </div>

      {goals.length === 0 ? (
        <EmptyState
          testId="goals-empty-state"
          icon={Target}
          title="Aún no tienes metas"
          description="Crea una meta para ahorrar para algo — solo, o con tu pareja, familia o amigos."
          ctaLabel="Crear meta"
          onCtaClick={() => setShowCreate(true)}
        />
      ) : (
        <div className="space-y-1" data-testid="goals-list">
          {goals.map((g) => <GoalRow key={g.id} goal={g} onOpen={() => setOpenGoalId(g.id)} />)}
        </div>
      )}

      <GoalDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        onSaved={(g) => { onRefresh(); setOpenGoalId(g.id); }}
      />
    </div>
  );
}

function GoalRow({ goal, onOpen }) {
  const { hidden } = useAmountsVisibility();
  const Icon = getIcon(goal.icon);
  const activeMembers = goal.members.filter(m => m.active).length;
  const when = daysLeftLabel(goal);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left rounded-xl px-2.5 py-3 -mx-2.5 transition-colors hover:bg-white/[0.04] active:scale-[0.99]"
      data-testid={`goal-row-${goal.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(goal.color) }}>
            <Icon className="w-4 h-4" style={{ color: catColor(goal.color) }} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-200 truncate">{goal.name}</p>
            <p className="text-xs text-slate-500 flex items-center gap-1.5">
              {goal.shared ? <><Users className="w-3 h-3" /> {activeMembers} {activeMembers === 1 ? 'persona' : 'personas'}</> : 'Personal'}
              {when && <span>· {when}</span>}
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-semibold text-slate-100 metric-value">{formatAmount(goal.total, hidden)}</p>
          <p className="text-[11px] text-slate-500 metric-value">de {formatAmount(goal.target_amount, hidden)}</p>
        </div>
      </div>
      <ProgressTrack pct={goal.progress_pct} color={goal.color} />
      <p className="text-[11px] text-slate-500 mt-1.5">
        {Math.round(goal.progress_pct)}%
        {goal.suggested_per_period ? ` · sugerido ${formatAmount(goal.suggested_per_period, hidden)} por ${periodPer(goal.period)}` : ''}
      </p>
    </button>
  );
}

function GoalDetail({ goalId, onBack, onRefresh, refreshKey }) {
  const { hidden } = useAmountsVisibility();
  const { openPaywall } = usePaywall();
  const [goal, setGoal] = useState(null);
  const [showContribute, setShowContribute] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [memberFilter, setMemberFilter] = useState(null);
  const [nudging, setNudging] = useState(null);

  // Ref so a parent re-render (new onBack identity) doesn't refetch.
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const load = useCallback(async () => {
    try {
      setGoal(await getGoal(goalId));
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo cargar la meta'));
      onBackRef.current();
    }
  }, [goalId]);
  // refreshKey: App bumps it on notification taps / returning to the app.
  useEffect(() => { load(); }, [load, refreshKey]);

  const afterChange = (updated) => {
    if (updated) setGoal(updated); else load();
    onRefresh();
  };

  if (!goal) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-violet-500/30 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }

  const Icon = getIcon(goal.icon);
  const when = daysLeftLabel(goal);
  const targetDate = formatTargetDate(goal.target_date);
  const showMembers = goal.shared || goal.members.length > 1;
  const history = memberFilter ? goal.contributions.filter(c => c.user_id === memberFilter) : goal.contributions;
  const filteredMember = memberFilter ? goal.members.find(m => m.user_id === memberFilter) : null;
  const myId = goal.me;

  const handleInvite = async () => {
    if (goal.invite_link) return shareInvite(goal);
    // Personal goal → turning it shared needs Pro (paywall on 402).
    try {
      const updated = await updateGoal(goal.id, { shared: true });
      afterChange(updated);
      shareInvite(updated);
    } catch (e) {
      if (isPaywallError(e)) openPaywall(e.response.data.detail);
      else toast.error(friendlyError(e, 'No se pudo crear la invitación'));
    }
  };

  const handleDeleteContribution = async (c) => {
    if (!window.confirm(`¿Eliminar tu aporte de ${formatAmount(c.amount, false)}? El monto vuelve a tu balance.`)) return;
    try {
      await deleteTransaction(c.id);
      toast.success('Aporte eliminado');
      afterChange();
    } catch {
      toast.error('No se pudo eliminar el aporte');
    }
  };

  // "Recordar": push a reminder to a member who's behind their period.
  const handleNudge = async (m) => {
    setNudging(m.user_id);
    try {
      const r = await nudgeGoalMember(goal.id, m.user_id);
      if (r.sent) toast.success(`Le recordamos a ${m.name.split(' ')[0]}`);
      else toast.message(r.message);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo enviar el recordatorio'));
    } finally {
      setNudging(null);
    }
  };

  const handleDelete = async () => {
    const others = goal.members.filter(m => m.active).length > 1;
    if (!window.confirm(`¿Eliminar la meta "${goal.name}"? Se eliminan todos sus aportes${others ? ' (también los de las otras personas)' : ''} y el dinero vuelve a cada balance. No se puede deshacer.`)) return;
    try {
      await deleteGoal(goal.id);
      toast.success('Meta eliminada');
      onRefresh();
      onBack();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo eliminar'));
    }
  };

  const handleLeave = async () => {
    if (!window.confirm(`¿Salir de "${goal.name}"? Tus aportes siguen contando para la meta.`)) return;
    try {
      await leaveGoal(goal.id);
      toast.success('Saliste de la meta');
      onRefresh();
      onBack();
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo salir'));
    }
  };

  return (
    <div className="space-y-10" data-testid="goal-detail">
      {/* Header — back arrow like Categorías */}
      <div className="flex items-center gap-2 min-w-0">
        <button
          type="button"
          onClick={onBack}
          aria-label="Volver"
          data-testid="goal-back-btn"
          className="hit-44 w-10 h-10 -ml-2 rounded-xl flex items-center justify-center text-slate-300 hover:text-white hover:bg-white/5 transition-colors active:scale-90 shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(goal.color) }}>
          <Icon className="w-4 h-4" style={{ color: catColor(goal.color) }} />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white truncate">{goal.name}</h1>
      </div>

      {/* Hero — same hierarchy as the Dashboard balance */}
      <div data-testid="goal-hero">
        <p className="text-[13px] font-medium text-slate-500">{goal.shared ? 'Ahorrado entre todos' : 'Ahorrado'}</p>
        <p className="mt-1 text-5xl sm:text-6xl font-semibold leading-none tracking-[-0.03em] text-white metric-value">
          {formatAmount(goal.total, hidden)}
        </p>
        <p className="mt-2 text-sm text-slate-400 metric-value">de {formatAmount(goal.target_amount, hidden)}</p>
        <div className="mt-5 max-w-sm">
          <ProgressTrack pct={goal.progress_pct} color={goal.color} height="h-2" />
          <p className="mt-2 text-xs text-slate-500">
            {Math.round(goal.progress_pct)}%
            {when && ` · ${when}`}
            {targetDate && !goal.completed && ` (${targetDate})`}
          </p>
        </div>
        {goal.suggested_per_period && (
          <p className="mt-4 text-sm text-slate-300" data-testid="goal-suggested">
            Aporte sugerido: <span className="font-semibold text-white metric-value">{formatAmount(goal.suggested_per_period, hidden)}</span> por {periodPer(goal.period)}
            {showMembers && goal.suggested_per_member && goal.suggested_per_member !== goal.suggested_per_period && (
              <span className="text-slate-500"> · ≈ {formatAmount(goal.suggested_per_member, hidden)} por persona</span>
            )}
          </p>
        )}
        <div className="flex items-center gap-2.5 mt-7">
          <Button
            data-testid="goal-contribute-btn"
            onClick={() => setShowContribute(true)}
            className="bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full h-11 px-5 text-sm font-semibold transition-colors active:scale-[0.97]"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Aportar
          </Button>
          {(goal.is_owner || goal.invite_link) && (
            <button
              type="button"
              data-testid="goal-invite-btn"
              onClick={handleInvite}
              className="h-11 px-4 rounded-full flex items-center gap-1.5 bg-white/[0.06] hover:bg-white/10 text-violet-300 text-sm font-medium transition-colors active:scale-[0.97]"
            >
              <UserPlus className="w-4 h-4" strokeWidth={1.75} /> Invitar
            </button>
          )}
        </div>
      </div>

      {/* Participants — per-person total, share, count, average */}
      {showMembers && (
        <section data-testid="goal-members">
          <h2 className="text-lg font-semibold tracking-tight text-white">Participantes</h2>
          <p className="text-xs text-slate-500 mt-0.5 mb-3">Toca a alguien para ver solo sus aportes</p>
          <div className="space-y-1">
            {goal.members.map(m => {
              const active = memberFilter === m.user_id;
              const isMe = m.user_id === myId;
              return (
                <div key={m.user_id} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setMemberFilter(active ? null : m.user_id)}
                  className={`flex-1 min-w-0 flex items-center justify-between gap-3 rounded-xl px-2.5 py-2.5 -ml-2.5 text-left transition-colors ${active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'}`}
                  data-testid={`goal-member-${m.user_id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={m.name} photo={m.photo} />
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${m.active ? 'text-slate-200' : 'text-slate-500'}`}>
                        {m.name}
                        {m.role === 'owner' && <span className="text-slate-500 font-normal"> · creó la meta</span>}
                        {!m.active && <span className="font-normal"> · salió</span>}
                      </p>
                      <p className="text-xs text-slate-500">
                        {m.count} {m.count === 1 ? 'aporte' : 'aportes'}
                        {m.count > 0 && <> · prom. {formatAmount(m.average, hidden)}</>}
                        {m.behind && (
                          <span className="text-amber-400"> · {m.count > 0 ? `sin aportar hace ${m.days_since_last} días` : 'aún no aporta'}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-semibold text-slate-100 metric-value">{formatAmount(m.total, hidden)}</p>
                    <p className="text-[11px] text-slate-500 tabular-nums">{Math.round(m.share_pct)}%</p>
                  </div>
                </button>
                {/* Only for others who are behind their contribution period. */}
                {m.behind && !isMe && (
                  <button
                    type="button"
                    onClick={() => handleNudge(m)}
                    disabled={nudging === m.user_id}
                    aria-label={`Recordar a ${m.name}`}
                    data-testid={`goal-nudge-${m.user_id}`}
                    className="hit-44 shrink-0 h-9 px-3 rounded-full flex items-center gap-1.5 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15 text-xs font-medium transition-colors active:scale-[0.97] disabled:opacity-50"
                  >
                    <BellRing className="w-3.5 h-3.5" strokeWidth={1.75} /> Recordar
                  </button>
                )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Contribution history — everyone's, for this goal only */}
      <section data-testid="goal-history">
        <div className="flex items-end justify-between gap-3 mb-3">
          <h2 className="text-lg font-semibold tracking-tight text-white">Aportes</h2>
          {filteredMember && (
            <button
              type="button"
              onClick={() => setMemberFilter(null)}
              className="hit-44 inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-medium bg-violet-500/15 text-violet-300"
            >
              {filteredMember.name} <X className="w-3 h-3" />
            </button>
          )}
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Todavía no hay aportes{filteredMember ? ` de ${filteredMember.name}` : ''}.</p>
        ) : (
          <div className="space-y-1">
            {history.map(c => (
              <div key={c.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`goal-contribution-${c.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {showMembers ? (
                    <Avatar name={c.contributor_name} size="w-9 h-9" />
                  ) : (
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: catTint(goal.color) }}>
                      <Icon className="w-4 h-4" style={{ color: catColor(goal.color) }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">
                      {showMembers ? (c.mine ? 'Tú' : c.contributor_name) : (c.description || 'Aporte')}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      {showMembers && c.description ? `${c.description} · ` : ''}{formatRelativeTime(c.date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-sm font-semibold text-slate-100 metric-value">+{formatAmount(c.amount, hidden)}</span>
                  {c.mine && (
                    <button
                      type="button"
                      onClick={() => handleDeleteContribution(c)}
                      aria-label="Eliminar aporte"
                      className="hit-44 w-8 h-8 rounded-lg flex items-center justify-center text-slate-600 hover:text-rose-400 hover:bg-rose-400/10 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Quiet management actions at the end */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm">
        {goal.is_owner ? (
          <>
            <button type="button" onClick={() => setShowEdit(true)} className="text-violet-400 hover:text-violet-300 font-medium" data-testid="goal-edit-btn">Editar meta</button>
            <button type="button" onClick={handleDelete} className="text-rose-400/80 hover:text-rose-400 font-medium" data-testid="goal-delete-btn">Eliminar meta</button>
          </>
        ) : (
          <button type="button" onClick={handleLeave} className="text-rose-400/80 hover:text-rose-400 font-medium" data-testid="goal-leave-btn">Salir de la meta</button>
        )}
      </div>

      <ContributeDialog open={showContribute} onOpenChange={setShowContribute} goal={goal} onSaved={afterChange} />
      <GoalDialog open={showEdit} onOpenChange={setShowEdit} editGoal={goal} onSaved={afterChange} />
    </div>
  );
}

// "1.500,50" / "1500,5" / "$1500" → "1500.50": digits and one decimal point.
function cleanAmount(value) {
  let v = String(value);
  // With a comma, dots are thousands separators ("1.500,50").
  if (v.includes(',')) v = v.replace(/\./g, '').replace(',', '.');
  v = v.replace(/[^\d.]/g, '');
  const [whole, ...rest] = v.split('.');
  return rest.length ? `${whole}.${rest.join('').slice(0, 2)}` : whole;
}

const emptyGoalForm = () => ({ name: '', target_amount: '', target_date: '', period: 'monthly', icon: 'PiggyBank', color: '#8b5cf6', shared: false });

export function GoalDialog({ open, onOpenChange, editGoal, onSaved }) {
  const { openPaywall } = usePaywall();
  const [form, setForm] = useState(emptyGoalForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editGoal ? {
      name: editGoal.name, target_amount: String(editGoal.target_amount), target_date: editGoal.target_date || '',
      period: editGoal.period, icon: editGoal.icon, color: editGoal.color, shared: editGoal.shared,
    } : emptyGoalForm());
  }, [open, editGoal]);

  const canSave = form.name.trim() && parseFloat(form.target_amount) > 0 && !saving;
  const lockShared = editGoal && editGoal.shared && editGoal.members.filter(m => m.active).length > 1;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const data = {
      name: form.name.trim(), target_amount: parseFloat(form.target_amount), target_date: form.target_date || null,
      period: form.period, icon: form.icon, color: form.color, shared: form.shared,
    };
    try {
      const saved = editGoal ? await updateGoal(editGoal.id, data) : await createGoal(data);
      toast.success(editGoal ? 'Meta actualizada' : 'Meta creada');
      onOpenChange(false);
      onSaved(saved);
    } catch (e) {
      if (isPaywallError(e)) openPaywall(e.response.data.detail);
      else toast.error(friendlyError(e, 'No se pudo guardar la meta'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSheetContent data-testid="goal-dialog">
        <DialogDescription className="sr-only">{editGoal ? 'Modifica tu meta' : 'Crea una meta de ahorro'}</DialogDescription>
        <DialogSheetHeader
          title={editGoal ? 'Editar meta' : 'Nueva meta'}
          onCancel={() => onOpenChange(false)}
          action={
            <button
              type="button"
              data-testid="save-goal-btn"
              onClick={handleSave}
              disabled={!canSave}
              className="text-[15px] font-semibold text-violet-400 px-2 py-2 -mr-2 rounded-lg transition-colors active:scale-[0.97] disabled:opacity-40"
            >
              {saving ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : (editGoal ? 'Guardar' : 'Crear')}
            </button>
          }
        />
        <DialogSheetBody>
          <div className="space-y-2">
            <Label className="text-slate-300">Nombre</Label>
            <Input
              data-testid="goal-name-input"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              placeholder="Ej: Viaje a la playa"
              maxLength={60}
              className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Monto objetivo ($)</Label>
              <Input
                data-testid="goal-amount-input"
                // text + decimal keypad (not type=number): with a Spanish
                // keyboard "1500,50" left a number field empty or invalid.
                type="text"
                inputMode="decimal"
                autoComplete="off"
                value={form.target_amount}
                onChange={e => setForm(p => ({ ...p, target_amount: cleanAmount(e.target.value) }))}
                placeholder="Ej: 1500"
                className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Fecha objetivo</Label>
              <Input
                data-testid="goal-date-input"
                type="date"
                min={todayStr()}
                value={form.target_date}
                onChange={e => setForm(p => ({ ...p, target_date: e.target.value }))}
                className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white [color-scheme:dark]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">¿Cada cuánto aportas?</Label>
            <div className="grid grid-cols-4 gap-1 p-1 bg-slate-800/70 rounded-xl" data-testid="goal-period-toggle">
              {goalPeriods.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, period: p.value }))}
                  className={`hit-44 h-9 rounded-lg text-xs font-semibold transition-colors ${form.period === p.value ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-start justify-between gap-4 rounded-xl bg-white/[0.03] p-3.5">
            <div>
              <p className="text-sm font-medium text-slate-200">Meta compartida</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Ahorra con tu pareja, familia o amigos. Requiere Pro — quienes invites se unen gratis.
              </p>
            </div>
            <Switch
              data-testid="goal-shared-switch"
              checked={form.shared}
              disabled={lockShared}
              onCheckedChange={v => setForm(p => ({ ...p, shared: v }))}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Icono</Label>
            <div className="grid grid-cols-6 gap-2">
              {goalIcons.map(iconName => {
                const I = getIcon(iconName);
                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, icon: iconName }))}
                    className={`w-11 h-11 rounded-lg flex items-center justify-center transition-colors ${
                      form.icon === iconName ? 'bg-violet-500/20 text-violet-400 ring-1 ring-violet-500/50' : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                    }`}
                  >
                    <I className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Color</Label>
            <div className="flex gap-3 flex-wrap">
              {categoryColors.map(c => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Color ${c}`}
                  onClick={() => setForm(p => ({ ...p, color: c }))}
                  className={`hit-44 w-10 h-10 rounded-full transition-transform duration-200 ${form.color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900 scale-110' : ''}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
        </DialogSheetBody>
      </DialogSheetContent>
    </Dialog>
  );
}

function ContributeDialog({ open, onOpenChange, goal, onSaved }) {
  const { hidden } = useAmountsVisibility();
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [date, setDate] = useState(todayStr());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setAmount(''); setNote(''); setDate(todayStr()); }
  }, [open]);

  const canSave = parseFloat(amount) > 0 && !saving;
  const suggested = goal.suggested_per_member || goal.suggested_per_period;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const updated = await contributeToGoal(goal.id, {
        amount: parseFloat(amount),
        note: note.trim(),
        // Today → exact now (keeps ordering); a past day → noon local.
        date: date === todayStr() ? new Date().toISOString() : new Date(`${date}T12:00:00`).toISOString(),
      });
      toast.success(`Aporte de ${formatAmount(parseFloat(amount), false)} registrado`);
      onOpenChange(false);
      onSaved(updated);
    } catch (e) {
      toast.error(friendlyError(e, 'No se pudo registrar el aporte'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogSheetContent data-testid="contribute-dialog">
        <DialogDescription className="sr-only">Registra un aporte a {goal.name}</DialogDescription>
        <DialogSheetHeader
          title={`Aportar a ${goal.name}`}
          onCancel={() => onOpenChange(false)}
          action={
            <button
              type="button"
              data-testid="save-contribution-btn"
              onClick={handleSave}
              disabled={!canSave}
              className="text-[15px] font-semibold text-violet-400 px-2 py-2 -mr-2 rounded-lg transition-colors active:scale-[0.97] disabled:opacity-40"
            >
              {saving ? <div className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" /> : 'Guardar'}
            </button>
          }
        />
        <DialogSheetBody>
          <div className="flex items-center justify-center gap-1">
            <span className="text-3xl font-bold text-violet-400/60">$</span>
            <input
              data-testid="contribution-amount-input"
              type="number"
              inputMode="decimal"
              step="0.01"
              autoFocus
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="0.00"
              className="bg-transparent border-none outline-none text-5xl font-bold text-white placeholder:text-slate-700 w-[9ch] text-center tabular-nums"
            />
          </div>
          {suggested && (
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => setAmount(String(suggested))}
                className="hit-44 h-8 px-3.5 rounded-full text-xs font-medium bg-white/[0.05] text-slate-300 hover:bg-white/10 transition-colors"
              >
                Sugerido: {formatAmount(suggested, hidden)}
              </button>
            </div>
          )}
          <p className="text-xs text-slate-500 text-center">Este monto sale de tu balance, pero no cuenta como gasto.</p>
          <div className="space-y-2">
            <Label className="text-slate-300">Nota (opcional)</Label>
            <Input
              value={note}
              onChange={e => setNote(e.target.value)}
              maxLength={120}
              placeholder="Ej: quincena"
              className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white placeholder:text-slate-400"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">Fecha</Label>
            <Input
              type="date"
              max={todayStr()}
              value={date}
              onChange={e => setDate(e.target.value || todayStr())}
              className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white [color-scheme:dark]"
            />
          </div>
        </DialogSheetBody>
      </DialogSheetContent>
    </Dialog>
  );
}
